import React, { useRef, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface Point {
  x: number;  // Relative 0-1 range
  y: number;  // Relative 0-1 range
}

interface HandDrawnStroke {
  id: string;
  type: string;
  points: Point[];
  color: string;
  relativeWidth: number;  // Percentage
  relativeFontSize?: number;  // Percentage for text
  timestamp: string;
  text?: string;  // For text annotations
  stampType?: string;  // For stamps (e.g., "APPROVED", "REVIEWED")
  measurement?: string;  // For dimension annotations
  connectedToStart?: string;  // ID of stroke this connects to at start
  connectedToEnd?: string;  // ID of stroke this connects to at end
}

interface HandDrawnAnnotationsProps {
  isEnabled: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  currentTool: string;
  selectedColor: string;
  lineWidth: number; // Percentage of canvas width (0.1 to 10)
  fontSize: number; // Percentage of canvas width (0.5 to 5)
  highlightOpacity?: number; // Opacity for highlighter (0.1 to 1.0)
  estimationId: string;
  fileId: string;
  pageNumber: number;
  currentPageCalibration?: {
    canvasPercentage: number;
    realWorldValue: number;
    unit: string;
  };
  onUndo?: () => void;
  onRedo?: () => void;
  onClear?: () => void;
  onHistoryChange?: (canUndo: boolean, canRedo: boolean) => void;
  onDrop?: (e: React.DragEvent<HTMLCanvasElement>) => void;
  onContextMenu?: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  // Wire run props
  onWireRunComplete?: (points: Point[], totalLength: number, pageNumber: number) => void;
  isWireRunMode?: boolean;
  wireRunType?: { name: string; color: string } | null;
  onWireRunClick?: (clickX: number, clickY: number) => void; // Called when clicking in wire run mode to check for existing wire runs
  onWireRunContextMenu?: (e: React.MouseEvent, canFinish: boolean, pointCount: number, finishCallback: () => void, cancelCallback: () => void) => void; // Called when right-clicking during wire run drawing
  onRightClickPan?: (e: React.MouseEvent | MouseEvent, phase: 'start' | 'move' | 'end') => void; // Called for right-click pan events
  // Square footage props
  isSquareFootageMode?: boolean;
  onSquareFootageComplete?: (rect: { x: number; y: number; width: number; height: number }, area: number, pageNumber: number) => void;
  // UNIFIED SELECT: Parent-controlled selection for unified selection across both layers
  externalSelectedStrokeIds?: Set<string>; // Selection controlled by parent
  onStrokeSelectionChange?: (selectedIds: Set<string>, append: boolean) => void; // Notify parent of selection changes
  onStrokesQuery?: () => HandDrawnStroke[]; // Parent can query current strokes
}

export const HandDrawnAnnotations: React.FC<HandDrawnAnnotationsProps> = ({
  isEnabled,
  canvasRef,
  currentTool,
  selectedColor,
  lineWidth,
  fontSize,
  highlightOpacity = 0.3,
  estimationId,
  fileId,
  pageNumber,
  currentPageCalibration,
  onUndo,
  onRedo,
  onClear,
  onHistoryChange,
  onDrop,
  onContextMenu,
  onWireRunComplete,
  isWireRunMode = false,
  wireRunType = null,
  onWireRunClick,
  onWireRunContextMenu,
  onRightClickPan,
  isSquareFootageMode = false,
  onSquareFootageComplete,
  externalSelectedStrokeIds,
  onStrokeSelectionChange,
  onStrokesQuery
}) => {
  const handDrawnCanvasRef = useRef<HTMLCanvasElement>(null);
  const [strokes, setStrokes] = useState<HandDrawnStroke[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isErasing, setIsErasing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedStrokes, setSelectedStrokes] = useState<Set<string>>(new Set());
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [selectionBox, setSelectionBox] = useState<{start: Point, end: Point} | null>(null);
  const [currentPoints, setCurrentPoints] = useState<Point[]>([]);
  const [mousePos, setMousePos] = useState<Point | null>(null);
  const [hoveredStroke, setHoveredStroke] = useState<string | null>(null);
  
  // UNIFIED SELECT: Use external selection when provided, otherwise use local selection
  const activeSelectedStrokes = externalSelectedStrokeIds || selectedStrokes;
  const isUnifiedSelectMode = !!externalSelectedStrokeIds;
  
  // Expose strokes to parent for unified selection
  useEffect(() => {
    if (onStrokesQuery) {
      // Store reference for parent to query
      (window as any).getHandDrawnStrokes = () => strokes;
    }
  }, [strokes, onStrokesQuery]);
  const [textInput, setTextInput] = useState<string>('');
  const [isEditingText, setIsEditingText] = useState(false);
  const [textBoxBounds, setTextBoxBounds] = useState<{start: Point, end: Point} | null>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const currentPageRef = useRef<number>(pageNumber);
  const [history, setHistory] = useState<HandDrawnStroke[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [snapPoint, setSnapPoint] = useState<Point | null>(null);
  
  // Wire run polyline state
  const [isPolylineDrawing, setIsPolylineDrawing] = useState(false);
  const [polylinePoints, setPolylinePoints] = useState<Point[]>([]);
  
  // Square footage rectangle state
  const [sqftStartPoint, setSqftStartPoint] = useState<Point | null>(null);
  const [sqftCurrentPoint, setSqftCurrentPoint] = useState<Point | null>(null);
  const [isSqftDrawing, setIsSqftDrawing] = useState(false);
  
  // Right-click pan state (just tracking if active, parent handles actual panning)
  const [isRightClickPanning, setIsRightClickPanning] = useState(false);
  
  // Shift key tracking for 90-degree snap
  const shiftHeldRef = useRef(false);

  // Sync canvas size with PDF annotation canvas
  useEffect(() => {
    let mutationObserver: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let syncTimeout: NodeJS.Timeout | null = null;

    const syncCanvasDimensions = () => {
      const pdfCanvas = canvasRef.current;
      const handCanvas = handDrawnCanvasRef.current;
      
      if (!pdfCanvas || !handCanvas) return;

      // Match dimensions
      if (handCanvas.width !== pdfCanvas.width || handCanvas.height !== pdfCanvas.height) {
        handCanvas.width = pdfCanvas.width;
        handCanvas.height = pdfCanvas.height;
        
        const pdfStyles = window.getComputedStyle(pdfCanvas);
        handCanvas.style.width = pdfStyles.width;
        handCanvas.style.height = pdfStyles.height;
        
        // Redraw strokes
        redrawStrokes();
      }
    };

    // Watch for PDF canvas changes
    mutationObserver = new MutationObserver(() => {
      if (syncTimeout) clearTimeout(syncTimeout);
      syncTimeout = setTimeout(syncCanvasDimensions, 50);
    });

    if (canvasRef.current?.parentElement) {
      mutationObserver.observe(canvasRef.current.parentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['width', 'height', 'style']
      });
    }

    if (canvasRef.current) {
      resizeObserver = new ResizeObserver(syncCanvasDimensions);
      resizeObserver.observe(canvasRef.current);
    }

    syncCanvasDimensions();

    return () => {
      if (syncTimeout) clearTimeout(syncTimeout);
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [canvasRef, isEnabled]);

  // Helper: Check if two points are close enough to snap (relative coordinates)
  const pointsMatch = (p1: Point, p2: Point, tolerance: number = 0.02): boolean => {
    const distance = Math.sqrt(
      Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2)
    );
    return distance < tolerance;
  };

  // Helper: Find arrow/line endpoint near a point (for snapping)
  const findNearbyEndpoint = (point: Point): { stroke: HandDrawnStroke; pointIndex: number; point: Point } | null => {
    const snapTolerance = 0.02; // 2% of canvas size
    
    for (const stroke of strokes) {
      // Only snap to arrows and lines
      if (stroke.type === 'hand-arrow' || stroke.type === 'hand-line') {
        // Check both start and end points
        for (let i = 0; i < stroke.points.length; i++) {
          const p = stroke.points[i];
          if (pointsMatch(p, point, snapTolerance)) {
            return { stroke, pointIndex: i, point: p };
          }
        }
      }
    }
    return null;
  };

  // Helper: Check if a stroke endpoint has any outgoing connections
  const hasOutgoingConnection = (stroke: HandDrawnStroke, pointIndex: number): boolean => {
    const point = stroke.points[pointIndex];
    
    // Check if any other arrow/line starts from this point
    return strokes.some(otherStroke => 
      otherStroke.id !== stroke.id &&
      (otherStroke.type === 'hand-arrow' || otherStroke.type === 'hand-line') &&
      otherStroke.points.length > 0 &&
      pointsMatch(otherStroke.points[0], point)
    );
  };

  // Redraw all strokes on canvas
  const redrawStrokes = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      const canvas = handDrawnCanvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw all strokes
      strokes.forEach(stroke => {
        drawStroke(ctx, stroke, canvas.width, canvas.height);
      });

    // Draw current stroke preview
    if (isDrawing && currentPoints.length > 0 && currentTool !== 'hand-dimension') {
      const previewStroke: HandDrawnStroke = {
          id: 'preview',
          type: currentTool,
          points: currentPoints,
          color: selectedColor,
          relativeWidth: lineWidth,
          timestamp: new Date().toISOString()
        };
        drawStroke(ctx, previewStroke, canvas.width, canvas.height);
      }

      // Draw selection box
      if (isSelecting && selectionBox) {
        const x1 = selectionBox.start.x * canvas.width;
        const y1 = selectionBox.start.y * canvas.height;
        const x2 = selectionBox.end.x * canvas.width;
        const y2 = selectionBox.end.y * canvas.height;
        const width = x2 - x1;
        const height = y2 - y1;
        
        // Draw selection rectangle
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(x1, y1, width, height);
        ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
        ctx.fillRect(x1, y1, width, height);
        ctx.setLineDash([]);
      }

      // Draw eraser cursor
      if (currentTool === 'hand-eraser' && mousePos) {
        const absoluteX = mousePos.x * canvas.width;
        const absoluteY = mousePos.y * canvas.height;
        const absoluteRadius = (lineWidth / 100) * canvas.width;
        
        ctx.beginPath();
        ctx.arc(absoluteX, absoluteY, absoluteRadius, 0, 2 * Math.PI);
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Draw snap point indicator for arrow/line tools
      if ((currentTool === 'hand-arrow' || currentTool === 'hand-line') && snapPoint && !isDrawing) {
        const absoluteX = snapPoint.x * canvas.width;
        const absoluteY = snapPoint.y * canvas.height;
        const snapRadius = 10;
        
        // Draw outer circle
        ctx.beginPath();
        ctx.arc(absoluteX, absoluteY, snapRadius, 0, 2 * Math.PI);
        ctx.strokeStyle = '#10b981'; // Green to indicate snap
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // Draw inner dot
        ctx.beginPath();
        ctx.arc(absoluteX, absoluteY, 3, 0, 2 * Math.PI);
        ctx.fillStyle = '#10b981';
        ctx.fill();
      }

      // Draw wire run polyline preview
      if ((isWireRunMode || currentTool === 'hand-wire-run') && isPolylineDrawing && polylinePoints.length > 0) {
        // Compute effective mouse position (constrained to 90° when shift held)
        let effectiveMousePos = mousePos;
        if (shiftHeldRef.current && mousePos && polylinePoints.length > 0) {
          effectiveMousePos = constrainTo90Degrees(
            polylinePoints[polylinePoints.length - 1],
            mousePos
          );
        }

        // Ensure full opacity
        ctx.globalAlpha = 1;
        
        // Use visible line width for wire runs
        const wireLineWidth = 3;
        // Use wire type color if available, otherwise fall back to selected color
        const wireColor = wireRunType?.color || selectedColor;
        
        ctx.strokeStyle = wireColor;
        ctx.lineWidth = wireLineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Draw confirmed polyline segments
        if (polylinePoints.length >= 2) {
          ctx.beginPath();
          ctx.moveTo(polylinePoints[0].x * canvas.width, polylinePoints[0].y * canvas.height);
          for (let i = 1; i < polylinePoints.length; i++) {
            ctx.lineTo(polylinePoints[i].x * canvas.width, polylinePoints[i].y * canvas.height);
          }
          ctx.stroke();
        }
        
        // Draw preview line from last point to current mouse position
        if (effectiveMousePos) {
          const lastPoint = polylinePoints[polylinePoints.length - 1];
          ctx.beginPath();
          ctx.setLineDash([6, 4]);
          ctx.moveTo(lastPoint.x * canvas.width, lastPoint.y * canvas.height);
          ctx.lineTo(effectiveMousePos.x * canvas.width, effectiveMousePos.y * canvas.height);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        
        // Draw start marker with white outline for visibility
        const startPoint = polylinePoints[0];
        ctx.beginPath();
        ctx.arc(startPoint.x * canvas.width, startPoint.y * canvas.height, 5, 0, 2 * Math.PI);
        ctx.fillStyle = wireColor;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        
        // Draw dots at intermediate vertices
        if (polylinePoints.length > 2) {
          polylinePoints.slice(1, -1).forEach((point) => {
            ctx.beginPath();
            ctx.arc(point.x * canvas.width, point.y * canvas.height, 3, 0, 2 * Math.PI);
            ctx.fillStyle = wireColor;
            ctx.fill();
          });
        }
        
        // Draw current measurement - large and visible
        if (currentPageCalibration && polylinePoints.length >= 1 && effectiveMousePos) {
          const totalLength = calculatePolylineLength([...polylinePoints, effectiveMousePos]);
          const lastPoint = polylinePoints[polylinePoints.length - 1];
          const midX = ((lastPoint.x + effectiveMousePos.x) / 2) * canvas.width;
          const midY = ((lastPoint.y + effectiveMousePos.y) / 2) * canvas.height;
          
          // Draw measurement label - bigger and more visible
          ctx.font = 'bold 16px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          const text = `${totalLength.toFixed(1)} ${currentPageCalibration.unit}`;
          const textWidth = ctx.measureText(text).width + 20;
          
          // Background - colored with white border
          ctx.fillStyle = wireColor;
          ctx.roundRect(midX - textWidth / 2, midY - 14, textWidth, 28, 4);
          ctx.fill();
          
          // White border
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.roundRect(midX - textWidth / 2, midY - 14, textWidth, 28, 4);
          ctx.stroke();
          
          // Text
          ctx.fillStyle = '#fff';
          ctx.fillText(text, midX, midY);
        }
      }

      // Draw square footage rectangle preview
      if ((isSquareFootageMode || currentTool === 'hand-square-footage') && isSqftDrawing && sqftStartPoint && sqftCurrentPoint) {
        const sx = sqftStartPoint.x * canvas.width;
        const sy = sqftStartPoint.y * canvas.height;
        const sw = (sqftCurrentPoint.x - sqftStartPoint.x) * canvas.width;
        const sh = (sqftCurrentPoint.y - sqftStartPoint.y) * canvas.height;
        const sqftColor = '#9333EA';
        
        // Light fill
        ctx.globalAlpha = 0.06;
        ctx.fillStyle = sqftColor;
        ctx.fillRect(sx, sy, sw, sh);
        
        // 1-unit grid inside the rectangle
        if (currentPageCalibration) {
          const pxPerUnit = (currentPageCalibration.canvasPercentage / 100) * canvas.width / currentPageCalibration.realWorldValue;
          if (pxPerUnit > 5) {
            ctx.save();
            ctx.beginPath();
            ctx.rect(Math.min(sx, sx + sw), Math.min(sy, sy + sh), Math.abs(sw), Math.abs(sh));
            ctx.clip();
            
            ctx.globalAlpha = 0.18;
            ctx.strokeStyle = sqftColor;
            ctx.lineWidth = 0.5;
            ctx.setLineDash([3, 3]);
            
            const gridLeft = Math.min(sx, sx + sw);
            const gridTop = Math.min(sy, sy + sh);
            const gridRight = Math.max(sx, sx + sw);
            const gridBottom = Math.max(sy, sy + sh);
            
            for (let gx = gridLeft + pxPerUnit; gx < gridRight; gx += pxPerUnit) {
              ctx.beginPath();
              ctx.moveTo(gx, gridTop);
              ctx.lineTo(gx, gridBottom);
              ctx.stroke();
            }
            for (let gy = gridTop + pxPerUnit; gy < gridBottom; gy += pxPerUnit) {
              ctx.beginPath();
              ctx.moveTo(gridLeft, gy);
              ctx.lineTo(gridRight, gy);
              ctx.stroke();
            }
            
            ctx.setLineDash([]);
            ctx.restore();
          }
        }
        
        // Border
        ctx.globalAlpha = 0.6;
        ctx.strokeStyle = sqftColor;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([8, 4]);
        ctx.strokeRect(sx, sy, sw, sh);
        ctx.setLineDash([]);
        
        // Area label
        ctx.globalAlpha = 1;
        if (currentPageCalibration) {
          const rectW = Math.abs(sqftCurrentPoint.x - sqftStartPoint.x);
          const rectH = Math.abs(sqftCurrentPoint.y - sqftStartPoint.y);
          const area = calculateRectArea(rectW, rectH);
          const unit = currentPageCalibration.unit || 'ft';
          const label = `${area.toFixed(1)} sq ${unit}`;
          
          const cx = sx + sw / 2;
          const cy = sy + sh / 2;
          
          ctx.font = 'bold 14px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          const tw = ctx.measureText(label).width + 16;
          
          ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
          ctx.beginPath();
          ctx.roundRect(cx - tw / 2, cy - 12, tw, 24, 4);
          ctx.fill();
          
          ctx.fillStyle = '#fff';
          ctx.fillText(label, cx, cy);
        }
      }
    });
  };

  // Draw a single stroke
  const drawStroke = (ctx: CanvasRenderingContext2D, stroke: HandDrawnStroke, canvasWidth: number, canvasHeight: number) => {
    if (stroke.points.length === 0) return;

    // Convert relative points to absolute
    const absolutePoints = stroke.points.map(p => ({
      x: p.x * canvasWidth,
      y: p.y * canvasHeight
    }));

    const absoluteWidth = (stroke.relativeWidth / 100) * canvasWidth;
    const isSelected = activeSelectedStrokes.has(stroke.id);

    // Draw selection highlight first
    if (isSelected && stroke.type !== 'hand-text') {
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = absoluteWidth + 4;
      ctx.globalAlpha = 0.3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      if (stroke.type === 'hand-pen' || stroke.type === 'hand-highlight') {
        if (absolutePoints.length >= 2) {
          ctx.beginPath();
          ctx.moveTo(absolutePoints[0].x, absolutePoints[0].y);
          absolutePoints.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.stroke();
        }
      }
      
      ctx.globalAlpha = 1;
    }

    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = absoluteWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (stroke.type === 'hand-highlight') {
      ctx.globalAlpha = highlightOpacity;
    }

    if (stroke.type === 'hand-pen' || stroke.type === 'hand-highlight') {
      // Draw freehand stroke
      if (absolutePoints.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(absolutePoints[0].x, absolutePoints[0].y);
      absolutePoints.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    } else if (stroke.type === 'hand-line') {
      if (absolutePoints.length !== 2) return;
      
      // Draw selection highlight
      if (isSelected) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = absoluteWidth + 4;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.moveTo(absolutePoints[0].x, absolutePoints[0].y);
        ctx.lineTo(absolutePoints[1].x, absolutePoints[1].y);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = absoluteWidth;
      }
      
      ctx.beginPath();
      ctx.moveTo(absolutePoints[0].x, absolutePoints[0].y);
      ctx.lineTo(absolutePoints[1].x, absolutePoints[1].y);
      ctx.stroke();
    } else if (stroke.type === 'hand-arrow') {
      if (absolutePoints.length !== 2) return;
      
      // Draw selection highlight
      if (isSelected) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = absoluteWidth + 4;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.moveTo(absolutePoints[0].x, absolutePoints[0].y);
        ctx.lineTo(absolutePoints[1].x, absolutePoints[1].y);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = absoluteWidth;
      }
      
      ctx.beginPath();
      ctx.moveTo(absolutePoints[0].x, absolutePoints[0].y);
      ctx.lineTo(absolutePoints[1].x, absolutePoints[1].y);
      ctx.stroke();
      
      // Only draw arrowhead if endpoint has no outgoing connections
      // This creates continuous arrow paths where only terminal points have arrowheads
      const endpointIndex = 1; // End point of the arrow
      if (!hasOutgoingConnection(stroke, endpointIndex)) {
        drawArrowhead(ctx, absolutePoints[0], absolutePoints[1], stroke.color);
      }
    } else if (stroke.type === 'hand-rectangle') {
      if (absolutePoints.length !== 2) return;
      const width = absolutePoints[1].x - absolutePoints[0].x;
      const height = absolutePoints[1].y - absolutePoints[0].y;
      
      // Draw selection highlight
      if (isSelected) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(absolutePoints[0].x - 3, absolutePoints[0].y - 3, width + 6, height + 6);
        ctx.setLineDash([]);
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = absoluteWidth;
      }
      
      ctx.strokeRect(absolutePoints[0].x, absolutePoints[0].y, width, height);
    } else if (stroke.type === 'hand-ellipse') {
      if (absolutePoints.length !== 2) return;
      const centerX = (absolutePoints[0].x + absolutePoints[1].x) / 2;
      const centerY = (absolutePoints[0].y + absolutePoints[1].y) / 2;
      const radiusX = Math.abs(absolutePoints[1].x - absolutePoints[0].x) / 2;
      const radiusY = Math.abs(absolutePoints[1].y - absolutePoints[0].y) / 2;
      
      // Draw selection highlight
      if (isSelected) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radiusX + 3, radiusY + 3, 0, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = absoluteWidth;
      }
      
      ctx.beginPath();
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (stroke.type === 'hand-text') {
      // Draw text (no border, just text)
      if (absolutePoints.length < 2 || !stroke.text) return;
      const x = Math.min(absolutePoints[0].x, absolutePoints[1].x);
      const y = Math.min(absolutePoints[0].y, absolutePoints[1].y);
      const width = Math.abs(absolutePoints[1].x - absolutePoints[0].x);
      const height = Math.abs(absolutePoints[1].y - absolutePoints[0].y);
      
      // Only show selection highlight if selected
      if (activeSelectedStrokes.has(stroke.id)) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(x - 2, y - 2, width + 4, height + 4);
        ctx.setLineDash([]);
      }
      
      // Draw text with relative font size (no border or background)
      ctx.fillStyle = stroke.color;
      const canvasFontSizePx = ((stroke.relativeFontSize || 1.0) / 100) * canvasWidth;
      ctx.font = `${canvasFontSizePx}px Arial`;
      
      // Word wrap text
      const lines = stroke.text.split('\n');
      const lineHeight = canvasFontSizePx * 1.2;
      lines.forEach((line, i) => {
        ctx.fillText(line, x + 5, y + lineHeight * (i + 1));
      });
    } else if (stroke.type === 'hand-note') {
      // Draw sticky note icon
      if (absolutePoints.length < 1) return;
      const size = absoluteWidth * 5;
      ctx.fillStyle = '#FFD700';
      ctx.fillRect(absolutePoints[0].x, absolutePoints[0].y, size, size);
      ctx.strokeRect(absolutePoints[0].x, absolutePoints[0].y, size, size);
      if (stroke.text) {
        ctx.fillStyle = '#000';
        ctx.font = `${size * 0.3}px Arial`;
        ctx.fillText('📝', absolutePoints[0].x + size * 0.2, absolutePoints[0].y + size * 0.6);
      }
    } else if (stroke.type === 'hand-callout') {
      // Draw callout (text box with arrow)
      if (absolutePoints.length !== 2 || !stroke.text) return;
      // Draw arrow from point 0 to point 1
      ctx.beginPath();
      ctx.moveTo(absolutePoints[0].x, absolutePoints[0].y);
      ctx.lineTo(absolutePoints[1].x, absolutePoints[1].y);
      ctx.stroke();
      drawArrowhead(ctx, absolutePoints[0], absolutePoints[1], stroke.color);
      // Draw text box at end
      ctx.font = `${absoluteWidth * 3}px Arial`;
      ctx.fillStyle = stroke.color;
      ctx.fillText(stroke.text, absolutePoints[1].x + 10, absolutePoints[1].y);
    } else if (stroke.type === 'hand-stamp') {
      // Draw stamp
      if (absolutePoints.length < 1) return;
      const stampText = stroke.stampType || 'APPROVED';
      const size = absoluteWidth * 8;
      ctx.save();
      ctx.translate(absolutePoints[0].x, absolutePoints[0].y);
      ctx.rotate(-0.2);
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = absoluteWidth * 0.5;
      ctx.strokeRect(-size/2, -size/4, size, size/2);
      ctx.fillStyle = stroke.color;
      ctx.globalAlpha = 0.7;
      ctx.font = `bold ${size * 0.25}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(stampText, 0, size * 0.1);
      ctx.restore();
      ctx.textAlign = 'left';
    } else if (stroke.type === 'hand-dimension') {
      // Draw dimension line with measurement removed
      if (absolutePoints.length !== 2) return;
      // Draw wire/conduit run polyline
      if (absolutePoints.length < 2) return;
      
      // Ensure full opacity for wire runs
      ctx.globalAlpha = 1;
      
      // Use visible line width for wire runs
      const wireLineWidth = 3;
      ctx.lineWidth = wireLineWidth;
      ctx.strokeStyle = stroke.color;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Draw selection highlight if selected
      if (isSelected) {
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = wireLineWidth + 4;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.moveTo(absolutePoints[0].x, absolutePoints[0].y);
        for (let i = 1; i < absolutePoints.length; i++) {
          ctx.lineTo(absolutePoints[i].x, absolutePoints[i].y);
        }
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = wireLineWidth;
      }
      
      // Draw the polyline
      ctx.beginPath();
      ctx.moveTo(absolutePoints[0].x, absolutePoints[0].y);
      for (let i = 1; i < absolutePoints.length; i++) {
        ctx.lineTo(absolutePoints[i].x, absolutePoints[i].y);
      }
      ctx.stroke();
      
      // Draw small dots at vertices
      absolutePoints.forEach((point, index) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, index === 0 ? 4 : 3, 0, 2 * Math.PI);
        ctx.fillStyle = stroke.color;
        ctx.fill();
      });
      
      // Draw measurement label if available
      if (stroke.measurement) {
        const midIndex = Math.floor(absolutePoints.length / 2);
        const labelPoint = absolutePoints[midIndex];
        
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        const text = stroke.measurement;
        const textWidth = ctx.measureText(text).width + 16;
        
        // Background - solid black with good contrast
        ctx.fillStyle = stroke.color;
        ctx.roundRect(labelPoint.x - textWidth / 2, labelPoint.y - 12, textWidth, 24, 4);
        ctx.fill();
        
        // White border for visibility
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.roundRect(labelPoint.x - textWidth / 2, labelPoint.y - 12, textWidth, 24, 4);
        ctx.stroke();
        
        // Text - white on colored background
        ctx.fillStyle = '#fff';
        ctx.fillText(text, labelPoint.x, labelPoint.y);
      }
    }

    if (stroke.type === 'hand-highlight') {
      ctx.globalAlpha = 1;
    }
  };

  // Draw arrowhead - centered on tip so half extends beyond
  const drawArrowhead = (ctx: CanvasRenderingContext2D, from: Point, to: Point, color: string) => {
    const headLength = ctx.lineWidth * 4;
    const headWidth = ctx.lineWidth * 2.5;
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    
    // Tip extends BEYOND the endpoint by half the head length
    const tipX = to.x + (headLength / 2) * Math.cos(angle);
    const tipY = to.y + (headLength / 2) * Math.sin(angle);
    
    // Base is before the endpoint by half the head length
    const baseX = to.x - (headLength / 2) * Math.cos(angle);
    const baseY = to.y - (headLength / 2) * Math.sin(angle);
    
    const baseLeftX = baseX - headWidth * Math.sin(angle);
    const baseLeftY = baseY + headWidth * Math.cos(angle);
    
    const baseRightX = baseX + headWidth * Math.sin(angle);
    const baseRightY = baseY - headWidth * Math.cos(angle);
    
    // Draw filled triangle - 50% inside, 50% outside the line
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(baseLeftX, baseLeftY);
    ctx.lineTo(baseRightX, baseRightY);
    ctx.closePath();
    ctx.fill();
  };

  // Get mouse position as relative coordinates (0-1 range)
  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement> | MouseEvent): Point => {
    const canvas = handDrawnCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    
    // Get relative position (0-1 range)
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    return { x, y };
  };

  // Check if point is near a line segment
  const distanceToSegment = (point: Point, p1: Point, p2: Point): number => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const lengthSquared = dx * dx + dy * dy;
    
    if (lengthSquared === 0) {
      return Math.sqrt(Math.pow(point.x - p1.x, 2) + Math.pow(point.y - p1.y, 2));
    }
    
    let t = ((point.x - p1.x) * dx + (point.y - p1.y) * dy) / lengthSquared;
    t = Math.max(0, Math.min(1, t));
    
    const projX = p1.x + t * dx;
    const projY = p1.y + t * dy;
    
    return Math.sqrt(Math.pow(point.x - projX, 2) + Math.pow(point.y - projY, 2));
  };

  // Check if point is inside a stroke
  const findStrokeAtPoint = (point: Point): HandDrawnStroke | null => {
    const tolerance = 0.03; // 3% tolerance for clicking
    
    for (let i = strokes.length - 1; i >= 0; i--) {
      const stroke = strokes[i];
      
      // For text boxes, notes, stamps - check if click is inside bounds
      if ((stroke.type === 'hand-text' || stroke.type === 'hand-note' || stroke.type === 'hand-stamp') && stroke.points.length >= 2) {
        const x1 = Math.min(stroke.points[0].x, stroke.points[1].x);
        const x2 = Math.max(stroke.points[0].x, stroke.points[1].x);
        const y1 = Math.min(stroke.points[0].y, stroke.points[1].y);
        const y2 = Math.max(stroke.points[0].y, stroke.points[1].y);
        
        if (point.x >= x1 && point.x <= x2 && point.y >= y1 && point.y <= y2) {
          return stroke;
        }
      }
      
      // For pen/highlight strokes, check proximity to line segments
      if (stroke.type === 'hand-pen' || stroke.type === 'hand-highlight') {
        for (let j = 0; j < stroke.points.length - 1; j++) {
          const dist = distanceToSegment(point, stroke.points[j], stroke.points[j + 1]);
          if (dist < tolerance) {
            return stroke;
          }
        }
      }
      
      // For shapes (line, arrow, rectangle, ellipse, etc), check line segments
      if (stroke.type === 'hand-line' || stroke.type === 'hand-arrow' || 
          stroke.type === 'hand-dimension' || stroke.type === 'hand-callout') {
        if (stroke.points.length >= 2) {
          const dist = distanceToSegment(point, stroke.points[0], stroke.points[1]);
          if (dist < tolerance) {
            return stroke;
          }
        }
      }
      
      // For rectangles, check all four edges
      if (stroke.type === 'hand-rectangle' && stroke.points.length === 2) {
        const x1 = stroke.points[0].x;
        const y1 = stroke.points[0].y;
        const x2 = stroke.points[1].x;
        const y2 = stroke.points[1].y;
        
        const edges = [
          [{ x: x1, y: y1 }, { x: x2, y: y1 }],
          [{ x: x2, y: y1 }, { x: x2, y: y2 }],
          [{ x: x2, y: y2 }, { x: x1, y: y2 }],
          [{ x: x1, y: y2 }, { x: x1, y: y1 }]
        ];
        
        for (const edge of edges) {
          const dist = distanceToSegment(point, edge[0], edge[1]);
          if (dist < tolerance) {
            return stroke;
          }
        }
      }
      
      // For ellipses, simplified check - just check points
      if (stroke.type === 'hand-ellipse' && stroke.points.length === 2) {
        const centerX = (stroke.points[0].x + stroke.points[1].x) / 2;
        const centerY = (stroke.points[0].y + stroke.points[1].y) / 2;
        const radiusX = Math.abs(stroke.points[1].x - stroke.points[0].x) / 2;
        const radiusY = Math.abs(stroke.points[1].y - stroke.points[0].y) / 2;
        
        // Check if point is near the ellipse perimeter
        const dx = (point.x - centerX) / radiusX;
        const dy = (point.y - centerY) / radiusY;
        const distance = Math.abs(Math.sqrt(dx * dx + dy * dy) - 1);
        
        if (distance < tolerance / Math.min(radiusX, radiusY)) {
          return stroke;
        }
      }
    }
    
    return null;
  };

  // Constrain a point to 90-degree angles (horizontal/vertical) relative to an anchor
  const constrainTo90Degrees = (anchor: Point, target: Point): Point => {
    const dx = target.x - anchor.x;
    const dy = target.y - anchor.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
      return { x: target.x, y: anchor.y };
    } else {
      return { x: anchor.x, y: target.y };
    }
  };

  // Calculate area of a rectangle using calibration (relative coords 0-1)
  const calculateRectArea = (rectWidth: number, rectHeight: number): number => {
    if (!currentPageCalibration) return 0;
    const widthPercent = rectWidth * 100;
    const heightPercent = rectHeight * 100;
    const realWidth = (widthPercent / currentPageCalibration.canvasPercentage) * currentPageCalibration.realWorldValue;
    const realHeight = (heightPercent / currentPageCalibration.canvasPercentage) * currentPageCalibration.realWorldValue;
    return realWidth * realHeight;
  };

  // Calculate total length of polyline points using calibration
  const calculatePolylineLength = (points: Point[]): number => {
    if (points.length < 2 || !currentPageCalibration) return 0;
    
    let total = 0;
    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      const dy = points[i].y - points[i - 1].y;
      const segmentPercent = Math.sqrt(dx * dx + dy * dy) * 100;
      const realLength = (segmentPercent / currentPageCalibration.canvasPercentage) * currentPageCalibration.realWorldValue;
      total += realLength;
    }
    return total;
  };

  // Handle mouse down
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement> | MouseEvent) => {
    if (!isEnabled) return;
    
    // If text editing is active and clicking outside, finish text
    if (isEditingText) {
      handleTextSubmit();
      return;
    }
    
    // Right-click temporary pan - notify parent to handle panning
    if ('button' in e && e.button === 2 && currentTool === 'select' && !isWireRunMode && onRightClickPan) {
      e.preventDefault();
      console.log('🖱️ Right-click pan started in HandDrawnAnnotations');
      setIsRightClickPanning(true);
      onRightClickPan(e as React.MouseEvent, 'start');
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    const point = getMousePos(e);
    
    // Handle square footage mode (click-drag to draw rectangle)
    if (isSquareFootageMode || currentTool === 'hand-square-footage') {
      setSqftStartPoint(point);
      setSqftCurrentPoint(point);
      setIsSqftDrawing(true);
      return;
    }

    // Handle wire run polyline mode (click to add points)
    if (isWireRunMode || currentTool === 'hand-wire-run') {
      if (!isPolylineDrawing) {
        // First click - notify parent to check for existing wire run annotations
        // The parent can switch to wires tab if clicking on an existing wire run
        if (onWireRunClick) {
          const canvas = canvasRef.current;
          if (canvas) {
            // Convert relative point to canvas coordinates for the parent
            onWireRunClick(point.x * canvas.width, point.y * canvas.height);
          }
        }
        
        // Start a new polyline
        console.log('🔌 Starting wire run at:', point);
        setIsPolylineDrawing(true);
        setPolylinePoints([point]);
      } else {
        // Subsequent clicks - add point to polyline
        let newPoint = point;
        if ((e as any).shiftKey && polylinePoints.length > 0) {
          newPoint = constrainTo90Degrees(polylinePoints[polylinePoints.length - 1], point);
        }
        console.log('🔌 Adding wire run point:', newPoint);
        setPolylinePoints(prev => [...prev, newPoint]);
      }
      redrawStrokes();
      return;
    }
    
    if (currentTool === 'hand-eraser') {
      setIsErasing(true);
      eraseStrokeAtPoint(point);
      return;
    }
    
    // Handle Select tool (for selecting/dragging annotations)
    if (currentTool === 'select') {
      const clickedStroke = findStrokeAtPoint(point);
      if (clickedStroke) {
        // Check if shift is pressed for multi-select
        const isShiftPressed = (e as any).shiftKey;
        
        if (isShiftPressed) {
          // Toggle selection
          const newSet = new Set(activeSelectedStrokes);
          if (newSet.has(clickedStroke.id)) {
            newSet.delete(clickedStroke.id);
          } else {
            newSet.add(clickedStroke.id);
          }
          
          if (isUnifiedSelectMode && onStrokeSelectionChange) {
            onStrokeSelectionChange(newSet, true); // append mode
          } else {
            setSelectedStrokes(newSet);
          }
        } else {
          // Single select
          const newSet = new Set([clickedStroke.id]);
          
          if (isUnifiedSelectMode && onStrokeSelectionChange) {
            onStrokeSelectionChange(newSet, false); // replace mode
          } else {
            setSelectedStrokes(newSet);
          }
        }
        
        setIsDragging(true);
        setDragStart(point);
        return;
      } else {
        // UNIFIED SELECT: Clicked empty space - start selection box
        // Clicks pass through to counting layer via pointer-events: none
        setIsSelecting(true);
        setSelectionBox({start: point, end: point});
        setDragStart(point);
        return;
      }
    }
    
    // Hand tool (drag) - events pass through to PDF viewer for panning
    // (handled by pointerEvents: 'none' in canvas style)
    
    // For text-based tools, just click and type
    if (currentTool === 'hand-text' || currentTool === 'hand-note' || currentTool === 'hand-stamp') {
      setTextBoxBounds({start: point, end: {x: point.x + 0.15, y: point.y + 0.08}}); // Default size
      setIsEditingText(true);
      setTimeout(() => textInputRef.current?.focus(), 100);
      return;
    }
    
    // For arrow and line tools, check if we're starting near an existing endpoint
    let startPoint = point;
    if (currentTool === 'hand-arrow' || currentTool === 'hand-line') {
      const nearbyEndpoint = findNearbyEndpoint(point);
      if (nearbyEndpoint) {
        // Snap to the exact endpoint position for precise connection
        startPoint = nearbyEndpoint.point;
        console.log('🎯 Snapped to existing endpoint:', nearbyEndpoint.stroke.id);
      }
      // Clear snap point indicator when starting to draw
      setSnapPoint(null);
    }
    
    setIsDrawing(true);
    setCurrentPoints([startPoint]);
  };

  // Handle mouse move
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement> | MouseEvent) => {
    if (!isEnabled) return;
    
    // Skip normal handling during right-click pan (document listeners handle it)
    if (isRightClickPanning) {
      return;
    }
    
    const point = getMousePos(e);
    setMousePos(point);
    shiftHeldRef.current = !!(e as any).shiftKey;
    
    // Update hover state for select tool only
    if (currentTool === 'select' && !isDragging && !isSelecting) {
      const hoveredStroke = findStrokeAtPoint(point);
      setHoveredStroke(hoveredStroke?.id || null);
    }
    
    // Handle square footage rectangle preview
    if ((isSquareFootageMode || currentTool === 'hand-square-footage') && isSqftDrawing) {
      setSqftCurrentPoint(point);
      redrawStrokes();
      return;
    }

    // Handle wire run polyline preview
    if ((isWireRunMode || currentTool === 'hand-wire-run') && isPolylineDrawing) {
      redrawStrokes(); // Redraw to show current polyline with preview line to mouse
      return;
    }
    
    // Update snap point indicator for arrow/line tools when not drawing
    if ((currentTool === 'hand-arrow' || currentTool === 'hand-line') && !isDrawing) {
      const nearbyEndpoint = findNearbyEndpoint(point);
      if (nearbyEndpoint) {
        setSnapPoint(nearbyEndpoint.point);
      } else {
        setSnapPoint(null);
      }
      redrawStrokes(); // Redraw to show/hide snap indicator
    } else if (snapPoint) {
      // Clear snap point if tool changed
      setSnapPoint(null);
    }
    
    // Handle selection box drag
    if (isSelecting && dragStart) {
      setSelectionBox({start: dragStart, end: point});
      redrawStrokes();
      return;
    }
    
    // Handle dragging selected strokes
    if (isDragging && dragStart && selectedStrokes.size > 0) {
      const deltaX = point.x - dragStart.x;
      const deltaY = point.y - dragStart.y;
      
      setStrokes(prev => prev.map(stroke => {
        if (activeSelectedStrokes.has(stroke.id)) {
          return {
            ...stroke,
            points: stroke.points.map(p => ({
              x: p.x + deltaX,
              y: p.y + deltaY
            }))
          };
        }
        return stroke;
      }));
      
      setDragStart(point);
      setHasChanges(true);
      redrawStrokes();
      return;
    }
    
    // Handle eraser drag
    if (isErasing && currentTool === 'hand-eraser') {
      eraseStrokeAtPoint(point);
      return;
    }
    
    // Show eraser cursor
    if (currentTool === 'hand-eraser') {
      redrawStrokes();
      return;
    }
    
    if (!isDrawing) return;
    
    // Add point to current path for pen/highlight
    if (currentTool === 'hand-pen' || currentTool === 'hand-highlight') {
      setCurrentPoints(prev => [...prev, point]);
    } else if (currentTool === 'hand-line' || currentTool === 'hand-arrow' || 
               currentTool === 'hand-rectangle' || currentTool === 'hand-ellipse' ||
               currentTool === 'hand-callout' || currentTool === 'hand-dimension') {
      // For shapes, update the end point
      setCurrentPoints([currentPoints[0], point]);
    }
    
    // Redraw with preview
    redrawStrokes();
  };

  // Save current strokes to history
  const saveToHistory = () => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push([...strokes]); // Deep copy of strokes array
      if (newHistory.length > 20) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    
    setHistoryIndex(prev => Math.min(prev + 1, 19));
  };

  // Undo
  const undo = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    setStrokes([...history[newIndex]]);
    setHistoryIndex(newIndex);
    setHasChanges(true);
    redrawStrokes();
  };

  // Redo
  const redo = () => {
    if (historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    setStrokes([...history[newIndex]]);
    setHistoryIndex(newIndex);
    setHasChanges(true);
    redrawStrokes();
  };

  // Undo last wire point (for wire run mode)
  const undoWirePoint = () => {
    if (polylinePoints.length > 1) {
      console.log('⬅️ Undoing last wire point');
      setPolylinePoints(prev => prev.slice(0, -1));
      redrawStrokes();
      return true;
    }
    return false;
  };

  // Split points into continuous segments
  const splitIntoSegments = (remaining: Point[], original: Point[]): Point[][] => {
    if (remaining.length === 0) return [];
    
    const segments: Point[][] = [];
    let currentSegment: Point[] = [remaining[0]];
    
    for (let i = 1; i < remaining.length; i++) {
      const prevPoint = remaining[i - 1];
      const currPoint = remaining[i];
      
      // Find indices in original array
      const prevOriginalIndex = original.findIndex(p => p.x === prevPoint.x && p.y === prevPoint.y);
      const currOriginalIndex = original.findIndex(p => p.x === currPoint.x && p.y === currPoint.y);
      
      // Check if points were consecutive in original stroke
      if (currOriginalIndex - prevOriginalIndex === 1) {
        // Consecutive - add to current segment
        currentSegment.push(currPoint);
      } else {
        // Gap detected - save current segment and start new one
        if (currentSegment.length > 1) {
          segments.push(currentSegment);
        }
        currentSegment = [currPoint];
      }
    }
    
    // Add final segment
    if (currentSegment.length > 1) {
      segments.push(currentSegment);
    }
    
    return segments;
  };

  // Erase stroke points at a location (precise erasing)
  const eraseStrokeAtPoint = (point: Point) => {
    const eraserRadius = lineWidth / 100; // Relative radius
    const updatedStrokes: HandDrawnStroke[] = [];
    let anyChange = false;
    
    strokes.forEach(stroke => {
      // For pen/highlight strokes - remove points within eraser radius
      if (stroke.type === 'hand-pen' || stroke.type === 'hand-highlight') {
        // Keep only points outside the eraser circle
        const remainingPoints = stroke.points.filter(p => {
          const distance = Math.sqrt(
            Math.pow(p.x - point.x, 2) + Math.pow(p.y - point.y, 2)
          );
          return distance >= eraserRadius;
        });
        
        // Check if any points were removed
        if (remainingPoints.length < stroke.points.length) {
          anyChange = true;
          
          // Split into continuous segments
          const segments = splitIntoSegments(remainingPoints, stroke.points);
          
          // Create new stroke for each segment
          segments.forEach((segment, idx) => {
            if (segment.length > 1) {
              updatedStrokes.push({
                ...stroke,
                id: `${stroke.id}-split-${idx}-${Date.now()}`,
                points: segment
              });
            }
          });
        } else {
          // No points removed - keep stroke as is
          updatedStrokes.push(stroke);
        }
      } else {
        // For shapes (line, arrow, rectangle, ellipse) - check if any point is hit
        const hitStroke = stroke.points.some(p => {
          const distance = Math.sqrt(
            Math.pow(p.x - point.x, 2) + Math.pow(p.y - point.y, 2)
          );
          return distance < eraserRadius;
        });
        
        if (hitStroke) {
          anyChange = true;
          // Don't add stroke - it's erased
        } else {
          updatedStrokes.push(stroke);
        }
      }
    });
    
    if (anyChange) {
      // Only save to history on first erase of this drag
      if (!isErasing) {
        saveToHistory();
      }
      setStrokes(updatedStrokes);
      setHasChanges(true);
      redrawStrokes();
    }
  };


  // Handle text input submission
  const handleTextSubmit = () => {
    if (!textBoxBounds || !textInput.trim()) {
      setIsEditingText(false);
      setTextInput('');
      setTextBoxBounds(null);
      return;
    }

    const newStroke: HandDrawnStroke = {
      id: `stroke-${Date.now()}`,
      type: currentTool,
      points: [textBoxBounds.start, textBoxBounds.end],
      color: selectedColor,
      relativeWidth: lineWidth,
      relativeFontSize: fontSize,
      timestamp: new Date().toISOString(),
      text: textInput.trim(),
      stampType: currentTool === 'hand-stamp' ? textInput.trim().toUpperCase() : undefined
    };
    
    saveToHistory();
    setStrokes(prev => [...prev, newStroke]);
    setHasChanges(true);
    setIsEditingText(false);
    setTextInput('');
    setTextBoxBounds(null);
    redrawStrokes();
  };

  // Handle mouse up
  const handleMouseUp = () => {
    if (!isEnabled) return;
    
    // Right-click pan end is handled by document listener
    if (isRightClickPanning) {
      return;
    }
    
    // Handle square footage rectangle completion
    if ((isSquareFootageMode || currentTool === 'hand-square-footage') && isSqftDrawing && sqftStartPoint && sqftCurrentPoint) {
      const rectWidth = Math.abs(sqftCurrentPoint.x - sqftStartPoint.x);
      const rectHeight = Math.abs(sqftCurrentPoint.y - sqftStartPoint.y);
      
      if (rectWidth > 0.005 && rectHeight > 0.005) {
        const rect = {
          x: Math.min(sqftStartPoint.x, sqftCurrentPoint.x),
          y: Math.min(sqftStartPoint.y, sqftCurrentPoint.y),
          width: rectWidth,
          height: rectHeight,
        };
        const area = calculateRectArea(rectWidth, rectHeight);
        if (onSquareFootageComplete) {
          onSquareFootageComplete(rect, area, pageNumber);
        }
      }
      
      setSqftStartPoint(null);
      setSqftCurrentPoint(null);
      setIsSqftDrawing(false);
      redrawStrokes();
      return;
    }
    
    // Handle selection box release
    if (isSelecting && selectionBox) {
      // Find all strokes within the selection box
      const minX = Math.min(selectionBox.start.x, selectionBox.end.x);
      const maxX = Math.max(selectionBox.start.x, selectionBox.end.x);
      const minY = Math.min(selectionBox.start.y, selectionBox.end.y);
      const maxY = Math.max(selectionBox.start.y, selectionBox.end.y);
      
      const selectedIds = new Set<string>();
      strokes.forEach(stroke => {
        // Check if any point of the stroke is within the box
        const isInBox = stroke.points.some(p => 
          p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY
        );
        if (isInBox) {
          selectedIds.add(stroke.id);
        }
      });
      
      if (isUnifiedSelectMode && onStrokeSelectionChange) {
        onStrokeSelectionChange(selectedIds, false);
      } else {
        setSelectedStrokes(selectedIds);
      }
      setIsSelecting(false);
      setSelectionBox(null);
      setDragStart(null);
      redrawStrokes();
      return;
    }
    
    if (isDragging) {
      setIsDragging(false);
      setDragStart(null);
      saveToHistory();
      return;
    }
    
    if (isErasing) {
      setIsErasing(false);
      // Save to history after finishing erase drag
      saveToHistory();
      return;
    }
    
    if (!isDrawing) return;
    
    setIsDrawing(false);
    
    if (currentPoints.length > 0) {
      // For callout, need text input
      if (currentTool === 'hand-callout' && currentPoints.length === 2) {
        setTextBoxBounds({start: currentPoints[0], end: currentPoints[1]});
        setIsEditingText(true);
        setCurrentPoints([]);
        setTimeout(() => textInputRef.current?.focus(), 100);
        return;
      } else if (currentTool === 'hand-dimension' && currentPoints.length === 2) {
        const distance = Math.sqrt(
          Math.pow(currentPoints[1].x - currentPoints[0].x, 2) +
          Math.pow(currentPoints[1].y - currentPoints[0].y, 2)
        );
        const percentage = distance * 100;
        
        let measurement;
        if (currentPageCalibration) {
          // Calculate real-world measurement using calibration
          const realWorld = (percentage / currentPageCalibration.canvasPercentage) * 
                            currentPageCalibration.realWorldValue;
          measurement = `${realWorld.toFixed(2)} ${currentPageCalibration.unit}`;
          console.log('📏 Dimension with calibration:', {
            canvasPercent: percentage.toFixed(2),
            realWorld: realWorld.toFixed(2),
            unit: currentPageCalibration.unit
          });
        } else {
          // Fallback to percentage if no calibration
          measurement = `${percentage.toFixed(1)}%`;
          console.log('⚠️ Dimension without calibration, showing percentage:', percentage.toFixed(1));
        }
        
        const newStroke: HandDrawnStroke = {
          id: `stroke-${Date.now()}`,
          type: currentTool,
          points: [...currentPoints],
          color: selectedColor,
          relativeWidth: lineWidth,
          relativeFontSize: fontSize,
          timestamp: new Date().toISOString(),
          measurement: measurement
        };
        
        saveToHistory();
        setStrokes(prev => [...prev, newStroke]);
        setHasChanges(true);
      } else {
        // Create normal stroke
        const newStroke: HandDrawnStroke = {
          id: `stroke-${Date.now()}`,
          type: currentTool,
          points: [...currentPoints],
          color: selectedColor,
          relativeWidth: lineWidth,
          relativeFontSize: fontSize,
          timestamp: new Date().toISOString()
        };
        
        saveToHistory();
        setStrokes(prev => [...prev, newStroke]);
        setHasChanges(true);
      }
    }
    
    setCurrentPoints([]);
    redrawStrokes();
  };

  // Save hand-drawn strokes to Supabase (fast JSON storage)
  const saveHandDrawnAnnotations = async (targetPage?: number, strokesToSave?: HandDrawnStroke[]) => {
    if (!estimationId || !fileId) return;

    const pageToSave = targetPage !== undefined ? targetPage : pageNumber;
    const dataToSave = strokesToSave !== undefined ? strokesToSave : strokes;

    try {
      // If no strokes, delete the record instead of saving empty
      if (dataToSave.length === 0) {
        console.log(`🗑️ Deleting annotations for page ${pageToSave}...`);
        
        const { error } = await supabase
          .from('hand_drawn_annotations')
          .delete()
          .eq('estimation_id', estimationId)
          .eq('file_id', fileId)
          .eq('page_number', pageToSave);

        if (error) {
          console.error('❌ Error deleting:', error);
        } else {
          console.log(`✅ Annotations deleted for page ${pageToSave}!`);
          if (pageToSave === pageNumber) {
            setHasChanges(false);
          }
        }
        return;
      }

      console.log(`💾 Saving ${dataToSave.length} strokes for page ${pageToSave}...`);

      const { error } = await supabase
        .from('hand_drawn_annotations')
        .upsert({
          estimation_id: estimationId,
          file_id: fileId,
          page_number: pageToSave,
          image_data: JSON.stringify(dataToSave)
        }, {
          onConflict: 'estimation_id,file_id,page_number'
        });

      if (error) {
        console.error('❌ Error saving strokes:', error);
      } else {
        console.log(`✅ Strokes saved instantly for page ${pageToSave}!`);
        if (pageToSave === pageNumber) {
          setHasChanges(false);
        }
      }
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  // Load hand-drawn strokes from Supabase
  const loadHandDrawnAnnotations = async () => {
    if (!estimationId || !fileId) return;

    try {
      console.log(`📂 Loading strokes for page ${pageNumber}...`);

      const { data } = await supabase
        .from('hand_drawn_annotations')
        .select('image_data')
        .eq('estimation_id', estimationId)
        .eq('file_id', fileId)
        .eq('page_number', pageNumber)
        .maybeSingle();

      if (data?.image_data) {
        const loadedStrokes = JSON.parse(data.image_data) as HandDrawnStroke[];
        console.log(`✅ Loaded ${loadedStrokes.length} strokes instantly!`);
        setStrokes(loadedStrokes);
        setHistory([loadedStrokes]);
        setHistoryIndex(0);
        redrawStrokes();
      } else {
        console.log(`📄 No strokes found for page ${pageNumber}`);
        setStrokes([]);
        setHistory([[]]);
        setHistoryIndex(0);
        redrawStrokes();
      }
    } catch (err) {
      console.error('Load error:', err);
      setStrokes([]);
      redrawStrokes();
    }
  };

  // Handle document-level events for right-click pan (parent handles actual panning)
  useEffect(() => {
    if (!isRightClickPanning) return;
    
    const handleDocumentMouseUp = (e: MouseEvent) => {
      if (isRightClickPanning) {
        console.log('🖱️ Right-click pan ended via document mouseup');
        setIsRightClickPanning(false);
        if (onRightClickPan) {
          onRightClickPan(e, 'end');
        }
      }
    };
    
    const handleDocumentMouseMove = (e: MouseEvent) => {
      if (isRightClickPanning && onRightClickPan) {
        onRightClickPan(e, 'move');
      }
    };
    
    document.addEventListener('mouseup', handleDocumentMouseUp);
    document.addEventListener('mousemove', handleDocumentMouseMove);
    
    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp);
      document.removeEventListener('mousemove', handleDocumentMouseMove);
    };
  }, [isRightClickPanning, onRightClickPan]);

  // Handle page changes
  useEffect(() => {
    const oldPage = currentPageRef.current;

    if (oldPage !== pageNumber && isEnabled) {
      // Save old page BEFORE updating ref (using old page number and old strokes)
      if (hasChanges && strokes.length > 0) {
        console.log(`📄 Page changing ${oldPage} → ${pageNumber}, saving old page...`);
        saveHandDrawnAnnotations(oldPage, strokes);
      }
      
      // NOW update the page ref
      currentPageRef.current = pageNumber;
      
      // Clear state before loading new page
      setHasChanges(false);
      setStrokes([]);
      setHistory([[]]);
      setHistoryIndex(0);
      
      // Load new page
      loadHandDrawnAnnotations();
    } else if (oldPage === pageNumber && isEnabled) {
      // First mount
      currentPageRef.current = pageNumber;
      loadHandDrawnAnnotations();
    }
  }, [pageNumber, isEnabled]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (hasChanges && strokes.length > 0) {
        saveHandDrawnAnnotations();
      }
    };
  }, [hasChanges, strokes]);

  // Auto-save after changes (debounced - 2 seconds)
  useEffect(() => {
    if (!hasChanges || !isEnabled || strokes.length === 0) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveHandDrawnAnnotations();
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [hasChanges, strokes]);

  // Redraw when strokes change
  useEffect(() => {
    if (isEnabled) {
      redrawStrokes();
    }
  }, [strokes, isEnabled]);

  // Delete selected strokes
  const deleteSelected = () => {
    if (selectedStrokes.size === 0) return;
    
    saveToHistory();
    setStrokes(prev => prev.filter(s => !activeSelectedStrokes.has(s.id)));
    if (isUnifiedSelectMode && onStrokeSelectionChange) {
      onStrokeSelectionChange(new Set(), false);
    } else {
      setSelectedStrokes(new Set());
    }
    setHasChanges(true);
    redrawStrokes();
  };

  // Keyboard shortcuts for delete and cancel polyline
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isEnabled) return;
      
      // Escape cancels wire run polyline
      if (e.key === 'Escape' && isPolylineDrawing) {
        e.preventDefault();
        console.log('🔌 Wire run cancelled with Escape');
        setIsPolylineDrawing(false);
        setPolylinePoints([]);
        redrawStrokes();
        return;
      }
      
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedStrokes.size > 0) {
        e.preventDefault();
        deleteSelected();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEnabled, selectedStrokes, isPolylineDrawing]);

  // Expose functions to parent
  useEffect(() => {
    (window as any).handDrawnUndo = undo;
    (window as any).handDrawnRedo = redo;
    (window as any).handDrawnClear = () => {
      saveToHistory();
      setStrokes([]);
      if (isUnifiedSelectMode && onStrokeSelectionChange) {
        onStrokeSelectionChange(new Set(), false);
      } else {
        setSelectedStrokes(new Set());
      }
      setHasChanges(true);
      redrawStrokes();
      // Pass empty array explicitly (setState is async)
      saveHandDrawnAnnotations(pageNumber, []);
    };
    (window as any).handDrawnDeleteSelected = deleteSelected;
    (window as any).handDrawnUndoWirePoint = undoWirePoint;
    (window as any).handDrawnGetWirePointCount = () => polylinePoints.length;
  }, [undo, redo, strokes, pageNumber, activeSelectedStrokes, deleteSelected, isUnifiedSelectMode, onStrokeSelectionChange, undoWirePoint, polylinePoints.length]);

  // Notify parent of history state changes
  useEffect(() => {
    if (onHistoryChange) {
      const canUndo = historyIndex > 0;
      const canRedo = historyIndex < history.length - 1;
      onHistoryChange(canUndo, canRedo);
    }
  }, [historyIndex, history, onHistoryChange]);

  if (!isEnabled) return null;

  return (
    <>
      <canvas
        ref={handDrawnCanvasRef}
        className="hand-drawn-canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDragOver={(e) => {
          e.preventDefault();
          console.log('🎯 Dragging over HandDrawnAnnotations canvas!');
        }}
        onDrop={(e) => {
          e.preventDefault();
          console.log('📥 Drop on HandDrawnAnnotations canvas, forwarding...');
          if (onDrop) {
            onDrop(e);
          }
        }}
        onContextMenu={(e) => {
          // When ACTIVELY drawing a polyline, show context menu with options
          if ((isWireRunMode || currentTool === 'hand-wire-run') && isPolylineDrawing) {
          e.preventDefault();
            console.log('🔌 Right-click during wire run drawing, showing context menu');
            
            // Create finish callback
            const finishCallback = () => {
              if (polylinePoints.length >= 2) {
                console.log('🔌 Finishing wire run with', polylinePoints.length, 'points');
                const totalLength = calculatePolylineLength(polylinePoints);
                const unit = currentPageCalibration?.unit || 'ft';
                console.log('📏 Total wire run length:', totalLength.toFixed(2), unit);
                
                if (onWireRunComplete) {
                  onWireRunComplete([...polylinePoints], totalLength, pageNumber);
                }
              }
              setIsPolylineDrawing(false);
              setPolylinePoints([]);
              redrawStrokes();
            };
            
            // Create cancel callback
            const cancelCallback = () => {
              console.log('🔌 Cancelling wire run');
              setIsPolylineDrawing(false);
              setPolylinePoints([]);
              redrawStrokes();
            };
            
            // Call the context menu callback to let parent show menu
            if (onWireRunContextMenu) {
              onWireRunContextMenu(e, polylinePoints.length >= 2, polylinePoints.length, finishCallback, cancelCallback);
            }
            return;
          }
          
          // For all other cases, forward to parent (or let it bubble)
          // DON'T preventDefault here so parent can show context menu
          console.log('🖱️ Right-click on HandDrawnAnnotations, forwarding...');
          if (onContextMenu) {
            onContextMenu(e);
          }
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          // UNIFIED SELECT: When in unified select mode with select tool, pass through clicks
          // so both layers can be selected. Otherwise, capture events for hand-drawn tools.
          pointerEvents: isEnabled && currentTool !== 'drag' && !(isUnifiedSelectMode && currentTool === 'select') ? 'auto' : 'none',
          zIndex: 50,
          cursor: isRightClickPanning ? 'grabbing' :
                  currentTool === 'drag' ? 'grab' : // Let PDF viewer handle pan cursor
                  isDragging ? 'move' :
                  isSelecting ? 'crosshair' :
                  currentTool === 'select' && hoveredStroke ? 'pointer' :
                  currentTool === 'select' ? 'default' :
                  currentTool === 'hand-eraser' ? 'crosshair' : 
                  currentTool === 'hand-text' ? 'text' :
                  currentTool === 'hand-note' ? 'pointer' :
                  currentTool === 'hand-stamp' ? 'pointer' :
                  (isWireRunMode || currentTool === 'hand-wire-run') ? 'crosshair' :
                  'crosshair'
        }}
      />
      
      {/* Text Input Box (Paint-style) - Fully relative sizing */}
      {isEditingText && textBoxBounds && (() => {
        const canvas = handDrawnCanvasRef.current;
        if (!canvas) return null;
        
        // Use canvas INTERNAL width (constant, doesn't change with zoom)
        // Then scale to match how canvas displays
        const rect = canvas.getBoundingClientRect();
        const scale = rect.width / canvas.width;
        
        // Font size as percentage of canvas (zoom-independent)
        const canvasFontPx = (fontSize / 100) * canvas.width;
        const fontSizePx = canvasFontPx * scale;
        
        console.log('📝 Text font calc:', {
          fontSize: fontSize,
          canvasWidth: canvas.width,
          visualWidth: rect.width,
          scale: scale,
          canvasFontPx: canvasFontPx,
          finalFontPx: fontSizePx
        });
        
        return (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: `${Math.min(textBoxBounds.start.x, textBoxBounds.end.x) * 100}%`,
              top: `${Math.min(textBoxBounds.start.y, textBoxBounds.end.y) * 100}%`,
              width: 'auto',
              height: 'auto',
              zIndex: 100,
              background: 'rgba(255, 255, 255, 0.95)',
              border: `2px dashed ${selectedColor}`,
              borderRadius: '4px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
              padding: '4px',
              maxWidth: `${rect.width * 0.5}px`
            }}
          >
            <textarea
              ref={textInputRef}
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  handleTextSubmit();
                }
                e.stopPropagation();
              }}
              placeholder={
                currentTool === 'hand-stamp' ? 'APPROVED' :
                currentTool === 'hand-note' ? 'Note...' :
                currentTool === 'hand-callout' ? 'Callout...' :
                'Type here...'
              }
              style={{
                width: 'auto',
                minWidth: `${fontSizePx * 4}px`,
                height: 'auto',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                resize: 'none',
                fontSize: `${fontSizePx}px`,
                fontFamily: 'Arial, sans-serif',
                color: selectedColor,
                lineHeight: '1.2',
                padding: 0,
                margin: 0,
                overflow: 'hidden'
              }}
              rows={1}
            />
          </div>
        );
      })()}
    </>
  );
};

